import React, { useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  RadioGroup,
  Grid,
  FormLabel,
  FormControl,
  FormControlLabel,
  Radio,
  Typography,
  TextField,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { grey } from "@mui/material/colors";
import { defaultStyle } from "components/Modals";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { updateProductionParkLimit } from "api/resources/productionPark";

interface EditLimitModalProps {
  productionParkId: number;
  productionParkType: string;
  limit: any;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  dispatch: any;
  setSnackbar: any;
}

const EditLimitModal = ({
  limit,
  productionParkType,
  setOpenModal,
  dispatch,
  setSnackbar,
}: EditLimitModalProps) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    toDateTime: dayjs(limit.toDateTime),
  });

  const handleSubmit = () => {
    setLoading(true);
    const postData = {
      toDateTime: dayjs(formData.toDateTime).utc().format(),
    };

    updateProductionParkLimit(limit.id, postData)
      .then(() => {
        dispatch();
        setLoading(false);
        setOpenModal(false);
        setSnackbar({
          type: "success",
          message: "Limitation updated successfully",
        });
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        setSnackbar({
          type: "error",
          message: error.response.data.message,
        });
      });
  };

  return (
    <Box sx={defaultStyle}>
      <Typography id="modal-modal-title" variant="h5" component="h5">
        Edit limitation
      </Typography>
      <Typography
        id="modal-modal-title"
        component="p"
        sx={{ color: grey[700], mt: "0.6rem", mb: "1rem" }}
      >
        You can only edit end time of the limitation. If you made a mistake, you
        should set the end time to current time and add a new limitation.
      </Typography>
      {productionParkType === "HYBRID" && (
        <FormControl sx={{ mt: 1 }} disabled>
          <FormLabel>Affected components</FormLabel>
          <RadioGroup
            row
            name="row-radio-buttons-limiting-park-type"
            value={limit.forecastType}
          >
            <FormControlLabel value="SUN" control={<Radio />} label="Solar" />
            <FormControlLabel value="WIND" control={<Radio />} label="Wind" />
          </RadioGroup>
        </FormControl>
      )}
      <Grid container spacing={0} sx={{ mb: "2rem" }}>
        <Grid item sm={8}>
          <Grid container sx={{ mt: "0.5rem", mb: "1rem" }}>
            <Grid item md={6}>
              <DateTimePicker
                label={"From"}
                slotProps={{
                  textField: { size: "small" },
                  clearButton: { size: "small" },
                }}
                minutesStep={15}
                skipDisabled
                disabled
                value={dayjs(limit.fromDateTime)}
                sx={{ mr: "0.8rem" }}
              />
            </Grid>
            <Grid item md={6}>
              <DateTimePicker
                label={"To"}
                sx={{ background: "white" }}
                slotProps={{ textField: { size: "small" } }}
                minDate={dayjs(limit.fromDateTime)}
                minutesStep={15}
                skipDisabled
                value={formData.toDateTime}
                onChange={(value) => {
                  const formDataCopy = { ...formData };

                  formDataCopy.toDateTime = value;

                  setFormData(formDataCopy);
                }}
              />
            </Grid>
          </Grid>
          <Box>
            <FormControl>
              <FormLabel>Limiting type</FormLabel>
              <RadioGroup row name="row-radio-buttons-group" value={limit.type}>
                <FormControlLabel
                  value="MAX_OUTPUT"
                  control={<Radio />}
                  disabled={true}
                  label="Max output"
                />
                <FormControlLabel
                  value="OUTPUT_AVAILABILITY"
                  control={<Radio />}
                  disabled={true}
                  label="Output availability"
                />
              </RadioGroup>
            </FormControl>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <TextField
                id="value"
                name="value"
                label="Value"
                type="number"
                disabled={true}
                size="small"
                InputProps={{
                  inputProps: {
                    min: 0,
                    max: limit.type === "MAX_OUTPUT" ? null : 100,
                  },
                  endAdornment: limit.type === "MAX_OUTPUT" ? "kW" : "%",
                }}
                sx={{ maxWidth: "125px" }}
                value={limit.value}
              />
            </FormControl>
          </Box>
        </Grid>
        <Grid item sm={12} sx={{ mt: "2rem" }}>
          <LoadingButton
            variant="contained"
            loading={loading}
            loadingIndicator="Saving..."
            onClick={() => handleSubmit()}
            disabled={loading || !formData.toDateTime}
          >
            Save limitation
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EditLimitModal;
